import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Globe } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { CTASection } from "@/components/CTASection";
import { usePageMeta } from "@/hooks/usePageMeta";

const CITIES = [
  { name: "Mumbai", line: "Brand, content and performance systems for India's fastest-moving market." },
  { name: "Delhi NCR", line: "Strategy and creative for brands competing in the capital's crowded feed." },
  { name: "Bengaluru", line: "Startup-speed branding, websites and AI content for the tech capital." },
  { name: "Hyderabad", line: "Growth marketing for healthcare, hospitality and tech brands on the rise." },
  { name: "Chennai", line: "Identity and content systems for brands that value craft and consistency." },
  { name: "Pune", line: "Digital presence for education, industrial and D2C brands scaling up." },
  { name: "Kolkata", line: "Creative storytelling for heritage brands and new ventures alike." },
  { name: "Ahmedabad", line: "Brand and e-commerce growth for India's most entrepreneurial city." },
  { name: "Jaipur", line: "Hospitality, handicraft and lifestyle brands built for the modern feed." },
];

export default function LocationsPage() {
  const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  usePageMeta({
    title: "Areas We Serve | BrandZenith — AI-Powered Brand Growth Studio",
    description:
      "BrandZenith serves brands across Mumbai, Delhi NCR, Bengaluru, Hyderabad, Chennai, Pune, Kolkata, Ahmedabad, Jaipur and worldwide — strategy, design, content, websites, SEO and AI marketing delivered remote-first.",
    path: "/locations",
    image: "/images/service-seo-local-seo.webp",
    schema: {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "BrandZenith",
      url: base,
      areaServed: CITIES.map((c) => ({ "@type": "City", name: c.name })),
    },
  });

  return (
    <>
      <Navbar />
      <main data-testid="locations-page">
        <PageHeader
          overline="Areas We Serve — Local Roots, Global Reach"
          title="Where We"
          accent="Move."
          copy="BrandZenith is headquartered in India and built remote-first — the same cinematic strategy, design and AI systems, delivered wherever your brand lives."
        />

        <section className="relative pb-24 lg:pb-32">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {CITIES.map((city, i) => (
                <Reveal key={city.name} delay={(i % 3) * 0.1} className="h-full">
                  <div
                    data-testid={`location-city-${city.name.toLowerCase().replace(/[^a-z]/g, "-")}`}
                    className="h-full glass-card rounded-lg p-7 transition-[border-color,box-shadow] duration-500 hover:border-[#6D00FF]/40 hover:shadow-[0_0_36px_-12px_rgba(109,0,255,0.5)]"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <span className="w-9 h-9 rounded-md border border-[#8B2CFF]/30 flex items-center justify-center text-[#C43CFF]">
                        <MapPin className="w-4 h-4" strokeWidth={1.5} />
                      </span>
                      <h2 className="font-display text-lg font-extrabold uppercase tracking-tight">
                        {city.name}
                      </h2>
                    </div>
                    <p className="text-sm text-[#A6A6B0] leading-relaxed">{city.line}</p>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal>
              <div
                data-testid="locations-global-band"
                className="mt-16 lg:mt-20 glass-card rounded-lg p-8 lg:p-12 flex flex-col lg:flex-row items-start lg:items-center gap-8 border-[#6D00FF]/20"
              >
                <span className="w-14 h-14 shrink-0 rounded-md border border-[#8B2CFF]/30 flex items-center justify-center text-[#C43CFF]">
                  <Globe className="w-6 h-6" strokeWidth={1.5} />
                </span>
                <div>
                  <h2 className="font-display text-xl sm:text-2xl font-extrabold uppercase tracking-tight mb-3">
                    Everywhere <span className="text-gradient-purple">Else.</span>
                  </h2>
                  <p className="text-sm sm:text-base text-[#A6A6B0] leading-relaxed max-w-2xl">
                    Our studio runs remote-first — shared boards, weekly reviews and production
                    systems that work across every time zone. If you have a brief, we have a
                    system. Distance has never stopped a good idea.
                  </p>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="mt-16 lg:mt-20 grid lg:grid-cols-2 gap-8 items-center border-t border-white/[0.06] pt-14">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#8B2CFF] mb-4">
                    Local SEO
                  </p>
                  <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase tracking-tight leading-[1.15]">
                    Want to own your city's <span className="text-gradient-purple">search results?</span>
                  </h2>
                  <p className="mt-4 text-sm sm:text-base text-[#A6A6B0] leading-relaxed">
                    Google Business Profile, maps visibility, reviews and 'near me' searches — our
                    SEO & Local SEO service puts your brand where your city is already looking.
                  </p>
                </div>
                <div className="lg:text-right">
                  <Link
                    to="/services/seo-local-seo"
                    data-testid="locations-seo-cta"
                    className="inline-flex items-center gap-2 bg-[#6D00FF] hover:bg-[#8B2CFF] text-white text-xs font-bold uppercase tracking-[0.16em] px-7 py-4 rounded-md transition-[background-color,box-shadow] duration-300 hover:shadow-[0_0_32px_-8px_rgba(139,44,255,0.8)]"
                  >
                    Explore SEO & Local SEO <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <CTASection />
      </main>
      <Footer />
    </>
  );
}
